import { useEffect } from 'react';
import { useRecoilValue } from 'recoil';

import { DSLoadingPanel } from '@demandstar/components/loading-panel';

import {
  Colors,
  ContentRowWrapper,
  DashboardRightColumn,
  HighlightedDetailsWrapper,
  HighlightedListWrapper,
  LeftColumnAbove,
} from '../../../shared/styles';
import { navigationPaths, requiredActionLabels } from '../../../shared/constants';
import {
  requiredActionListState,
  selectedRequiredActionIdState,
} from '../../../store/recoil/award-bid';
import { ArrowTitle } from '../../../components/layout/titles';
import { BidSummaryLabels } from '../../../shared/constants/bids';
import { BidSummaryPanel } from './BidSummaryPanel';
import { getDefaultLoadingMessage } from '../../../utils/message';
import { InfoPanel } from '../../../components/common/loading';
import { RequiredAction } from '../../../types/required-action';
import { RequiredActionDetailsPanelView } from './RequiredActionDetailsPanelView';
import { RequiredActionsListPanelView } from './RequiredActionsListPanelView';
import { selectedBidIdState } from '../../../store/recoil/bidState';
import { useRequiredActions } from '../../../shared/hooks/award-bid/useRequiredActions';
import { useResetOnUnmount } from '../../../shared/hooks/useResetOnUnmount';
import { useRouter } from 'src/shared/hooks';
import { useSetSelectedBidIdFromRoute } from '../../../shared/hooks/useSelectedBidId';

export function SupplierBidManagement() {
  /// Hooks
  const { history } = useRouter();

  // Update the selected bidId from the current url params.
  useSetSelectedBidIdFromRoute();

  const {
    refreshRequiredActions,
    requiredActionIncompleteList,
    requiredActionList,
    requiredActionsLoading,
  } = useRequiredActions();

  const selectedRequiredActionId = useRecoilValue(selectedRequiredActionIdState);

  const selectedBidId = useRecoilValue(selectedBidIdState);

  useResetOnUnmount(selectedBidIdState);
  useResetOnUnmount(requiredActionListState);

  // Refresh RequiredActions only for a valid bidId.
  useEffect(() => {
    if (selectedBidId > 0) {
      refreshRequiredActions();
    }
  }, [refreshRequiredActions, selectedBidId]);

  /**
   * Update the value of the `selectedRequiredActionIdState`
   * Navigate to the bid management view.
   * @param bidId: string
   * @param actionId: string
   */
  const handleActionClick = (requiredAction: RequiredAction) => {
    const link = `${navigationPaths.suppliers.bids}/${requiredAction.bidId}/${navigationPaths.subPaths.Management}/${requiredAction.actionId}`;
    history.push(link);
  };

  // TODO: Redux dispatch refreshes appear to reset results.
  // Solve this list problem in Recoil or move to direct queries.
  const requiredActionListFiltered = requiredActionList.filter(
    ({ bidId }) => bidId === selectedBidId,
  );

  const requiredActionCount = requiredActionListFiltered?.length || 0;

  const selectedRequiredAction = requiredActionListFiltered.find(
    action => action.actionId === selectedRequiredActionId,
  );

  const requiredActionCountDisplay =
    requiredActionIncompleteList.length > 0 ? `(${requiredActionIncompleteList.length})` : '';

  return (
    <>
      <ArrowTitle bgColor={Colors.orangeLight} label={requiredActionLabels.titlePlural} />
      <ContentRowWrapper>
        <>
          <LeftColumnAbove>
            <HighlightedListWrapper>
              {requiredActionsLoading ? (
                <DSLoadingPanel>
                  {getDefaultLoadingMessage(requiredActionLabels.titlePlural)}
                </DSLoadingPanel>
              ) : requiredActionCount > 0 ? (
                <RequiredActionsListPanelView
                  requiredActions={requiredActionListFiltered}
                  label={`${requiredActionLabels.titlePlural} ${requiredActionCountDisplay}`}
                  handleActionClick={handleActionClick}
                  selectedRequiredActionId={selectedRequiredAction?.actionId}
                />
              ) : (
                <InfoPanel message={requiredActionLabels.notFound} />
              )}
            </HighlightedListWrapper>
          </LeftColumnAbove>
          <DashboardRightColumn>
            <HighlightedDetailsWrapper>
              <BidSummaryPanel />
              {requiredActionCount > 0 && (
                <RequiredActionDetailsPanelView requiredAction={selectedRequiredAction} />
              )}
            </HighlightedDetailsWrapper>
          </DashboardRightColumn>
        </>
      </ContentRowWrapper>
    </>
  );
}
